<template>
  <div
    class="bg-surface rounded-extra-small flex flex-col overflow-hidden"
    :class="[expanded && 'rounded-large my-1']"
  >
    <div
      class="group rounded-b-extra-small relative flex w-full cursor-pointer flex-row items-center overflow-hidden px-3 py-2"
      @click="toggleExpanded"
    >
      <span class="text-label-large line-clamp-2">{{ question.text }}</span>
      <div class="ml-auto flex shrink-0 flex-row items-center gap-1 pl-2">
        <TendencyBage v-if="tendency" :tendency="tendency" />
        <span v-if="average != null" class="text-title-large w-14 text-right font-bold">{{
          useDisplayPercentage(average)
        }}</span>
        <span class="text-label-medium ml-4"
          >{{ numberOfAnswers }} {{ $t('common.answer', numberOfAnswers) }}</span
        >
        <BaseIcon size="20" class="ml-2" :class="[expanded && 'rotate-180']"
          >keyboard_arrow_down</BaseIcon
        >
      </div>
      <BaseStateLayer type="surface-neutral" />
    </div>
    <div v-if="expanded" class="flex flex-col py-2">
      <div class="flex flex-row gap-0.5 px-4">
        <div
          v-for="scaleItem in scale.items"
          :key="scaleItem.value"
          class="first:[&>div]:rounded-l-large last:[&>div]:rounded-r-large flex grow basis-0 flex-col items-center"
        >
          <div class="bg-surface-container rounded-small h-40 w-full">
            <LayoutItems
              :items="getMembersForValue(scaleItem.value)"
              :item-size="24"
              :padding="2"
              v-slot="{ item }"
            >
              <div
                class="relative"
                :class="[item.text && 'cursor-pointer']"
                @click="openTextFeedback(item)"
              >
                <BaseMemberAvatar size="24" :member="item" />
                <div
                  v-if="item.text"
                  class="bg-tertiary-container text-on-tertiary-container absolute -right-1 -bottom-1 flex h-4 w-4 items-center justify-center rounded-full"
                >
                  <BaseIcon size="10">chat_bubble</BaseIcon>
                </div>
              </div>
            </LayoutItems>
          </div>
          <span
            class="text-label-small text-on-surface-variant mt-0.5 inline-block w-full px-1 text-center"
            >{{ scaleItem.label }}</span
          >
        </div>
      </div>
    </div>
    <JournalAnalysisTextFeedbackDialog
      v-if="selectedMember"
      :is-open="isOpen"
      :member="selectedMember"
      :question-text="question.text"
      :feedback-text="selectedFeedbackText"
      :feedback-value="selectedFeedbackValue"
      @close="closeDialog"
    />
  </div>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue'
  import type { JSONContent } from '@tiptap/vue-3'
  import { useDisplayPercentage } from '@/core/composables/use-display-percentage'
  import BaseIcon from '../base/icon/BaseIcon.vue'
  import BaseStateLayer from '../base/state-layer/BaseStateLayer.vue'
  import BaseMemberAvatar from '../base/avatar/BaseMemberAvatar.vue'
  import type { Member } from '@/core/models/member/member'
  import type { Scale } from '@/core/models/scale'
  import LayoutItems from '../layout-items/LayoutItems.vue'
  import TendencyBage from '../base/tendency-badge/TendencyBage.vue'
  import JournalAnalysisTextFeedbackDialog from './JournalAnalysisTextFeedbackDialog.vue'
  import { useDialog } from '../base/dialog/use-dialog'

  type FeedbackMember = Member & {
    text: JSON | null
    value: number | null
  }

  interface Props {
    average: number | null
    question: {
      id: string
      text: string
    }
    feedbacks: ReadonlyArray<{
      id: string
      value: number | null
      text: JSON | null
      member: Member
    }>
    scale: Scale
    tendency: number
  }

  const props = defineProps<Props>()

  const expanded = ref(false)
  const selectedMember = ref<Member | null>(null)
  const selectedFeedbackText = ref<JSONContent | null>(null)
  const selectedFeedbackValue = ref<number | null>(null)
  const { isOpen, openDialog, closeDialog } = useDialog()

  const toggleExpanded = () => {
    expanded.value = !expanded.value
  }

  const numberOfAnswers = computed(() => {
    return props.feedbacks.length
  })

  const getMembersForValue = (value: number): FeedbackMember[] => {
    return props.feedbacks
      .filter((feedback) => feedback.value === value)
      .map((feedback) => {
        return { ...feedback.member, text: feedback.text, value: feedback.value }
      })
  }

  const openTextFeedback = (item: FeedbackMember) => {
    if (!item.text) return
    selectedMember.value = item
    selectedFeedbackText.value = item.text as JSONContent
    selectedFeedbackValue.value = item.value
    openDialog()
  }
</script>
